import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { Avatar, Badge, FlexGap, Paragraph, Skeleton, Tooltip, tokens } from '@treams/treams-ui';

const Copy = styled(Box)`
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 0;
	min-width: 0;
`;

const Name = styled(Paragraph)`
	display: block;
	color: ${tokens.text.primary};
	font-weight: ${tokens.typography.weights.semibold};
	line-height: ${tokens.typography.lineHeights.heading};
	margin: 0;
`;

const Status = styled(Paragraph)<{ $tone?: 'warning' | 'success' }>`
	display: block;
	color: ${(props) =>
		props.$tone === 'success'
			? tokens.success.text
			: props.$tone === 'warning'
				? tokens.warning.text
				: tokens.text.secondary};
	font-weight: ${tokens.typography.weights.medium};
	line-height: ${tokens.typography.lineHeights.heading};
	margin: 0;
`;

function StatusText(props: {
	requiresInput: boolean;
	status: graphql.EvaluationSubmissionStatus;
	signedAt?: string | null;
}) {
	const { t } = useTranslation();

	if (props.requiresInput && props.status === graphql.EvaluationSubmissionStatus.Draft) {
		return <Status $tone="warning">{t('evaluation.submission.status.draft')}</Status>;
	}

	if (props.requiresInput && props.status === graphql.EvaluationSubmissionStatus.Shared) {
		return <Status $tone="success">{t('evaluation.submission.status.shared')}</Status>;
	}

	if (
		!props.requiresInput &&
		(props.status === graphql.EvaluationSubmissionStatus.Draft ||
			props.status === graphql.EvaluationSubmissionStatus.Shared)
	) {
		return <Status>{t('evaluation.submission.status.noInputRequired')}</Status>;
	}

	if (props.status === graphql.EvaluationSubmissionStatus.Signed) {
		const signedText = (
			<Status $tone="success">
				<span style={{ cursor: 'pointer' }}>{t('evaluation.submission.status.signed')}</span>
			</Status>
		);

		if (props.signedAt) {
			return (
				<Tooltip
					content={t('conversations.party.signedAt', {
						date: moment(props.signedAt).format('LLL'),
					})}
					position="top"
				>
					{signedText}
				</Tooltip>
			);
		}

		return signedText;
	}

	return null;
}

function statusBadge(props: {
	requiresInput: boolean;
	status: graphql.EvaluationSubmissionStatus;
}) {
	if (props.status === graphql.EvaluationSubmissionStatus.Signed) {
		return <Badge.Success />;
	}

	if (props.requiresInput && props.status === graphql.EvaluationSubmissionStatus.Shared) {
		return <Badge.Success />;
	}

	if (props.requiresInput && props.status === graphql.EvaluationSubmissionStatus.Draft) {
		return <Badge.Pending />;
	}

	return undefined;
}

export default Skeleton(
	function ConversationParty(props: {
		result: graphql.EvaluationSubmissionResult;
		width?: number;
	}) {
		const badge = statusBadge({
			requiresInput: props.result.requiresInput,
			status: props.result.status,
		});

		return (
			<FlexGap alignItems="center" gap={tokens.gap.element} minWidth={0} width={props.width}>
				<Badge.Container badge={badge} flexGrow={0} flexShrink={0}>
					<Avatar size={36} user={props.result.user} />
				</Badge.Container>
				<Copy flexGrow={1} flexShrink={1}>
					<Name>
						{props.result.user.firstName} {props.result.user.lastName}
					</Name>
					<StatusText
						requiresInput={props.result.requiresInput}
						status={props.result.status}
						signedAt={props.result.signedAt}
					/>
				</Copy>
			</FlexGap>
		);
	},
	function ConversationPartySkeleton() {
		return (
			<FlexGap alignItems="center" gap={tokens.gap.element}>
				<Avatar.Skeleton size={36} flexGrow={0} flexShrink={0} />
				<Box flexGrow={1} flexShrink={1}>
					<Paragraph.Skeleton lines={1} maxWidth={140} />
					<Paragraph.Skeleton lines={1} maxWidth={140} />
				</Box>
			</FlexGap>
		);
	},
);
